import { type ReactNode } from "react";

import { FeatureLink } from "@softmax/web-features/navigation";

import { type SiteLink } from "@softmax/web-features/site/records";

import type { PillMenuData } from "./use-pill-menu";

import { isNavigationClick } from "./link-click";

import joinedStyles from "./account-pill.module.css";

type MenuStyles = typeof joinedStyles;

export type MenuLink = SiteLink & { current?: boolean };

export const menuChevron = (
  <svg width="9" height="5" viewBox="0 0 9 5" aria-hidden="true">
    <path
      d="m1 .5 3.5 3.5L8 .5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
    />
  </svg>
);

export type MenuProps = {
  data: PillMenuData;
  member: boolean;
  onNavigate: (href: string) => void;
};

export function Item({
  link,
  styles,
  onNavigate,
}: {
  link: MenuLink;
  styles: MenuStyles;
  onNavigate: (href: string) => void;
}) {
  const props = {
    href: link.href,
    className: styles.item,
    "aria-current": link.current ? ("page" as const) : undefined,
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (isNavigationClick(event)) onNavigate(link.href);
    },
  };
  return (
    <li>
      {link.external ? (
        <a {...props} rel="noreferrer">
          {link.label}
        </a>
      ) : (
        <FeatureLink {...props}>{link.label}</FeatureLink>
      )}
    </li>
  );
}

export function Group({
  heading,
  links,
  styles,
  onNavigate,
  children,
}: {
  heading: string;
  links: MenuLink[];
  styles: MenuStyles;
  onNavigate: (href: string) => void;
  children?: ReactNode;
}) {
  return (
    <li className={styles.group}>
      <span className={styles.heading}>{heading}</span>
      <ul className={styles.menu}>
        {links.map((link) => (
          <Item
            key={link.href + link.label}
            link={link}
            styles={styles}
            onNavigate={onNavigate}
          />
        ))}
        {children}
      </ul>
    </li>
  );
}

export function RetryRow({
  message,
  label,
  retry,
  styles,
}: {
  message: string;
  label: string;
  retry: () => void;
  styles: MenuStyles;
}) {
  return (
    <li className={styles.heading}>
      <output>{message}</output>
      <button type="button" className={styles.item} onClick={retry}>
        {label}
      </button>
    </li>
  );
}
